import { useCallback, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { useLogoTone } from "../lib/logoTone";

export function LogoResizeDialog({
  source,
  onClose,
  onSave,
}: {
  source: string;
  onClose: () => void;
  onSave: (file: File) => Promise<boolean | undefined>;
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(0.1);
  const [area, setArea] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const logoTone = useLogoTone(source);
  const onCropComplete = useCallback(
    (_: Area, pixels: Area) => setArea(pixels),
    [],
  );
  const onMediaLoaded = useCallback(
    ({ width, height }: { width: number; height: number }) => {
      const fit = Math.min(width, height) / Math.max(width, height);
      setMinZoom(fit * 0.5);
      setZoom(fit);
    },
    [],
  );
  const save = async () => {
    if (!area) return;
    setSaving(true);
    setError("");
    try {
      const image = new Image();
      image.crossOrigin = "anonymous";
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(
          () => reject(new Error("The logo could not load. Please try again.")),
          15000,
        );
        image.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        image.onerror = () => {
          clearTimeout(timeout);
          reject(new Error("The logo could not load. Please try again."));
        };
        image.src = source;
      });
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(area.width));
      canvas.height = Math.max(1, Math.round(area.height));
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not resize this logo.");
      ctx.drawImage(
        image,
        area.x,
        area.y,
        area.width,
        area.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (value) =>
            value
              ? resolve(value)
              : reject(new Error("Could not resize this logo.")),
          "image/png",
        ),
      );
      if (
        !(await onSave(
          new File([blob], "resized-logo.png", { type: "image/png" }),
        ))
      )
        throw new Error("Could not save your logo. Please try again.");
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle>Resize your logo</DialogTitle>
        <DialogDescription>
          Drag to position and use the slider to zoom. The circular area shows
          how your logo will appear.
        </DialogDescription>
        <div
          className={`relative h-[min(350px,45vh)] overflow-hidden rounded-lg ${logoTone === "light" ? "bg-neutral-800" : "bg-muted"}`}
          aria-busy={saving}
        >
          <Cropper
            image={source}
            crop={crop}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={3}
            aspect={1}
            cropShape="round"
            showGrid={false}
            objectFit="contain"
            restrictPosition={false}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            onMediaLoaded={onMediaLoaded}
          />
        </div>
        <label className="flex items-center gap-4 text-sm">
          Zoom
          <input
            aria-label="Logo zoom"
            type="range"
            min={minZoom}
            max={3}
            step={0.01}
            value={zoom}
            disabled={saving}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="min-w-0 flex-1 accent-brand"
          />
        </label>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="brand"
            disabled={saving || !area}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Apply changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
